// Dashboard store for caching dashboard-specific data
import { create } from 'zustand';
import { ChangelogItem } from '@types';
import { supabase } from '@config/supabase';

interface DashboardStore {
  changelog: ChangelogItem[];
  changelogVersion: string;
  isLoadingChangelog: boolean;
  lastFetchTime: number | null;
  isInitialized: boolean;
  hasRenderedOnce: boolean; // Track if dashboard has been rendered at least once
  
  // Actions
  loadChangelog: (force?: boolean) => Promise<void>;
  setChangelog: (changelog: ChangelogItem[], version: string) => void;
  setHasRenderedOnce: () => void;
  reset: () => void;
}

// Cache duration: 5 minutes (in milliseconds)
const CACHE_DURATION = 5 * 60 * 1000;

export const useDashboardStore = create<DashboardStore>((set, get) => ({
  changelog: [],
  changelogVersion: '21.2',
  isLoadingChangelog: false,
  lastFetchTime: null,
  isInitialized: false,
  hasRenderedOnce: false,

  loadChangelog: async (force = false) => {
    const state = get();
    
    // If already loading, don't start another load
    if (state.isLoadingChangelog) {
      return;
    }

    // Check if we have cached data that's still fresh
    if (!force && state.lastFetchTime && state.isInitialized) {
      const timeSinceFetch = Date.now() - state.lastFetchTime;
      if (timeSinceFetch < CACHE_DURATION) {
        console.log('✅ Using cached changelog data');
        return;
      }
    }

    set({ isLoadingChangelog: true });

    try {
      // Load changelog version
      const { data: versionData } = await supabase
        .from('changelog_settings')
        .select('version')
        .eq('id', 'current')
        .maybeSingle();
      
      let version = '21.2';
      if (versionData?.version) {
        version = versionData.version;
      }
      
      console.log('📥 Loading changelog from Supabase...');
      const { data, error } = await supabase
        .from('dashboard_content')
        .select('id, title, description, type, visible, archived, created_at, updated_at, icon')
        .eq('visible', true)
        .eq('archived', false)
        .order('created_at', { ascending: false })
        .limit(10);
      
      console.log('📊 Supabase response:', { data, error });

      if (error) {
        console.log('Dashboard content table not found, using fallback:', error);
        // Fallback to mock data
        const fallbackChangelog: ChangelogItem[] = [
          {
            id: '1',
            title: 'cloud-upload now drag and drop',
            icon: 'cloud',
            date: new Date(),
          },
          {
            id: '2',
            title: 'tags apply now even on the whole song',
            icon: 'tag',
            date: new Date(),
          },
          {
            id: '3',
            title: 'collaboration added',
            icon: 'users',
            badge: 'NEW',
            date: new Date(),
          },
        ];
        set({
          changelog: fallbackChangelog,
          changelogVersion: version,
          isLoadingChangelog: false,
          lastFetchTime: Date.now(),
          isInitialized: true,
        });
        return;
      }

      // Map database content to ChangelogItem format
      const mappedChangelog: ChangelogItem[] = (data || []).map((item) => ({
        id: item.id,
        title: item.title || '',
        description: item.description || '',
        icon: (item as any).icon || (item.type === 'change' ? 'tag' : item.type === 'news' ? 'cloud' : 'cloud'),
        date: new Date(item.created_at),
        badge: item.type === 'news' ? 'NEW' : undefined,
      }));
      
      console.log('✅ Loaded changelog from Supabase:', mappedChangelog);

      // Use real data if available, otherwise fallback
      if (mappedChangelog.length > 0) {
        set({
          changelog: mappedChangelog,
          changelogVersion: version,
          isLoadingChangelog: false,
          lastFetchTime: Date.now(),
          isInitialized: true,
        });
      } else {
        // Fallback to mock if no content
        const fallbackChangelog: ChangelogItem[] = [
          {
            id: '1',
            title: 'cloud-upload now drag and drop',
            icon: 'cloud',
            date: new Date(),
          },
        ];
        set({
          changelog: fallbackChangelog,
          changelogVersion: version,
          isLoadingChangelog: false,
          lastFetchTime: Date.now(),
          isInitialized: true,
        });
      }
    } catch (err) {
      console.error('Failed to load changelog:', err);
      // Fallback
      const fallbackChangelog: ChangelogItem[] = [
        {
          id: '1',
          title: 'cloud-upload now drag and drop',
          icon: 'cloud',
          date: new Date(),
        },
      ];
      set({
        changelog: fallbackChangelog,
        changelogVersion: '21.2',
        isLoadingChangelog: false,
        lastFetchTime: Date.now(),
        isInitialized: true,
      });
    }
  },

  setChangelog: (changelog: ChangelogItem[], version: string) => {
    set({
      changelog,
      changelogVersion: version,
      lastFetchTime: Date.now(),
      isInitialized: true,
    });
  },

  setHasRenderedOnce: () => {
    set({ hasRenderedOnce: true });
  },

  reset: () => {
    set({
      changelog: [],
      changelogVersion: '21.2',
      isLoadingChangelog: false,
      lastFetchTime: null,
      isInitialized: false,
      hasRenderedOnce: false,
    });
  },
}));

